import type {
  CollisionDetection,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import type { AdvisorProduct } from '../model/types';
import {
  closestCenter,
  MouseSensor,
  pointerWithin,
  rectIntersection,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useState } from 'react';
import {
  ADVISOR_DAY_PREFIX,
  ADVISOR_ITEM_PREFIX,
  ADVISOR_RAIL_DROP_ID,
  ADVISOR_TOTALS_ID,
  parseAdvisorDayDropId,
  parseAdvisorItemDragId,
  parseAdvisorSourceDragId,
} from '../model/dnd';

type SourcePreview = {
  productId: string;
  title: string;
  thumbnail: string | null;
};

type UseAdvisorDndInput = {
  products: AdvisorProduct[];
  onDropOnDay: (product: AdvisorProduct, dayId: string) => void;
  onDropOnRail: (product: AdvisorProduct) => void;
  onReorderItems: (activeItemId: string, overItemId: string) => void;
  /** Rail item released on a day's empty space; absent keeps day-only reorder. */
  onMoveItemToDay?: (itemId: string, dayId: string) => void;
};

export const advisorCollisionDetection: CollisionDetection = (args) => {
  // Over the pinned total the rail scrolls on unseen: drop nowhere. Read its
  // box live — dnd-kit shifts measured rects by scroll, but the bar is sticky.
  const totals = args.droppableContainers.find(
    (container) => container.id === ADVISOR_TOTALS_ID
  )?.node.current;
  const pointer = args.pointerCoordinates;
  if (totals && pointer) {
    const box = totals.getBoundingClientRect();
    if (
      pointer.x >= box.left &&
      pointer.x <= box.right &&
      pointer.y >= box.top &&
      pointer.y <= box.bottom
    ) {
      return [];
    }
  }
  const activeId = String(args.active.id);
  if (parseAdvisorItemDragId(activeId)) {
    const items = args.droppableContainers.filter((container) =>
      String(container.id).startsWith(ADVISOR_ITEM_PREFIX)
    );
    // Under the pointer: an item (reorder / move before it), else a day
    // (move to its end), else the nearest item as before.
    const overItem = pointerWithin({ ...args, droppableContainers: items });
    if (overItem.length > 0) return overItem;
    const days = args.droppableContainers.filter((container) =>
      String(container.id).startsWith(ADVISOR_DAY_PREFIX)
    );
    const overDay = pointerWithin({ ...args, droppableContainers: days });
    if (overDay.length > 0) return overDay;
    return closestCenter({ ...args, droppableContainers: items });
  }
  const days = args.droppableContainers.filter((container) => {
    const id = String(container.id);
    return id.startsWith(ADVISOR_DAY_PREFIX) || id === ADVISOR_RAIL_DROP_ID;
  });
  return rectIntersection({ ...args, droppableContainers: days });
};

export const useAdvisorDnd = ({
  products,
  onDropOnDay,
  onDropOnRail,
  onReorderItems,
  onMoveItemToDay,
}: UseAdvisorDndInput) => {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    })
  );
  const [preview, setPreview] = useState<SourcePreview | null>(null);

  const resolveProduct = (productId: string): AdvisorProduct | undefined =>
    products.find((product) => product.id === productId);

  const onDragStart = (event: DragStartEvent) => {
    const activeId = String(event.active.id);
    if (!parseAdvisorSourceDragId(activeId)) {
      setPreview(null);
      return;
    }
    const data = event.active.data.current as
      | {
          kind?: string;
          productId?: string;
          title?: string;
          thumbnail?: string | null;
        }
      | undefined;
    if (data?.kind === 'source-product' && data.productId) {
      setPreview({
        productId: data.productId,
        title: data.title ?? '—',
        thumbnail: data.thumbnail ?? null,
      });
    }
  };

  const onDragEnd = (event: DragEndEvent) => {
    setPreview(null);
    const activeId = String(event.active.id);
    const overId = event.over ? String(event.over.id) : null;
    if (!overId) return;
    const railItemId = parseAdvisorItemDragId(activeId);
    if (railItemId) {
      const overItemId = parseAdvisorItemDragId(overId);
      if (overItemId) {
        onReorderItems(railItemId, overItemId);
        return;
      }
      const overDayId = parseAdvisorDayDropId(overId);
      if (overDayId) onMoveItemToDay?.(railItemId, overDayId);
      return;
    }
    const productId = parseAdvisorSourceDragId(activeId);
    if (!productId) return;
    const product = resolveProduct(productId);
    if (!product) return;
    const dayId = parseAdvisorDayDropId(overId);
    if (dayId) {
      onDropOnDay(product, dayId);
      return;
    }
    if (overId === ADVISOR_RAIL_DROP_ID) onDropOnRail(product);
  };

  const onDragCancel = () => setPreview(null);

  return {
    sensors,
    collisionDetection: advisorCollisionDetection,
    onDragStart,
    onDragEnd,
    onDragCancel,
    preview,
  };
};
